import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';

import { API as BACKEND } from '../utils/apiBase';
const STORAGE_KEY = 'tt_chat_session';

// Icon that scales with the pill (uses 1em so it tracks fontSize)
const ConciergeIcon = () => (
  <MessageCircle
    style={{ width: '1.1em', height: '1.1em' }}
    strokeWidth={1.5}
  />
);

const ConciergeChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: "Hi — Timber & Thread concierge here. Ask me about delivery, sizing, materials, or anything else.",
    },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(
    () => (typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY)) || null
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    if (sessionId) localStorage.setItem(STORAGE_KEY, sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  // Fully hide the Emergent badge AND inject a cover shield that always sits
  // exactly under our round Concierge bubble, scaling with viewport.
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-tt-badge-override', 'true');
    styleEl.textContent = `
      #emergent-badge {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        width: 0 !important;
        height: 0 !important;
      }
    `;
    document.head.appendChild(styleEl);

    // Belt + braces: re-apply if the platform script ever re-injects the badge.
    const reHide = () => {
      const el = document.getElementById('emergent-badge');
      if (el) {
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('opacity', '0', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
      }
    };
    reHide();
    const interval = setInterval(reHide, 1500);
    return () => {
      clearInterval(interval);
      try { styleEl.remove(); } catch (e) { console.debug('[ConciergeChat] badge style cleanup skipped:', e); }
    };
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }, { role: 'assistant', text: '', streaming: true }]);
    setSending(true);

    try {
      const res = await fetch(`${BACKEND}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!res.body) throw new Error('No stream');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';
        for (const block of lines) {
          if (!block.startsWith('data: ')) continue;
          try {
            const payload = JSON.parse(block.slice(6));
            if (payload.session_id && !sessionId) setSessionId(payload.session_id);
            if (payload.delta) {
              setMessages((m) => {
                const last = m[m.length - 1];
                if (!last?.streaming) return m;
                return [
                  ...m.slice(0, -1),
                  { ...last, text: last.text + payload.delta },
                ];
              });
            }
            if (payload.done) {
              setMessages((m) =>
                m.map((x) => (x.streaming ? { ...x, streaming: false } : x))
              );
            }
            if (payload.error) {
              setMessages((m) => {
                const last = m[m.length - 1];
                if (!last?.streaming) return m;
                return [
                  ...m.slice(0, -1),
                  { ...last, text: "I'm having trouble right now. Please email info@timberandthread.com.au and we'll get back to you.", streaming: false },
                ];
              });
            }
          } catch (e) {
            // Partial/malformed SSE block — expected during streaming; log at debug.
            console.debug('[ConciergeChat] skipped malformed SSE block:', e);
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const last = m[m.length - 1];
        if (!last?.streaming) return m;
        return [
          ...m.slice(0, -1),
          { ...last, text: "I'm having trouble right now. Please email info@timberandthread.com.au.", streaming: false },
        ];
      });
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Closed: round chat bubble (no text). Sized to feel like a chat app icon.
          The Emergent badge is hidden via the global CSS rule injected above, so
          no masking shield is needed here. */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="concierge-open"
          aria-label="Open chat with Timber & Thread Concierge"
          style={{
            position: 'fixed',
            bottom: 'clamp(14px, 1.4vw, 22px)',
            right: 'clamp(14px, 1.4vw, 22px)',
            zIndex: 100000,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 'clamp(52px, 4.4vw, 64px)',
            height: 'clamp(52px, 4.4vw, 64px)',
            borderRadius: '9999px',
            background: '#33200F',
            color: '#F5EDDD',
            border: '1px solid #1E140B',
            cursor: 'pointer',
            boxShadow: '0 10px 30px rgba(30, 20, 11, 0.28)',
            transition: 'transform 200ms ease, background 200ms ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#1E140B';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#33200F';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <MessageCircle style={{ width: '40%', height: '40%' }} strokeWidth={1.5} />
        </button>
      )}

      {/* Open: chat panel — anchored bottom-right, also covers the Emergent pill */}
      {open && (
        <div
          data-testid="concierge-panel"
          style={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            width: 'min(380px, calc(100vw - 32px))',
            height: 'min(560px, calc(100vh - 48px))',
            background: '#F5EDDD',
            border: '1px solid #C9B596',
            borderRadius: 14,
            boxShadow: '0 25px 60px rgba(30, 20, 11, 0.28)',
            zIndex: 100000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
          }}
        >
          {/* Header */}
          <div style={{ background: '#33200F', color: '#F5EDDD', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, lineHeight: 1, marginBottom: 4 }}>
                Timber <span style={{ fontStyle: 'italic', color: '#E8DBC4' }}>&amp;</span> Thread Concierge
              </div>
              <div style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#E8DBC4', opacity: 0.8 }}>
                Replies within a minute
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="concierge-close"
              aria-label="Close chat"
              style={{ background: 'transparent', color: '#F5EDDD', border: 'none', cursor: 'pointer', padding: 4, display: 'inline-flex' }}
            >
              <X size={18} strokeWidth={1.5} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}`}
                style={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  padding: '10px 14px',
                  borderRadius: 14,
                  fontSize: 14,
                  lineHeight: 1.55,
                  background: m.role === 'user' ? '#33200F' : '#EDE3CF',
                  color: m.role === 'user' ? '#F5EDDD' : '#33200F',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {m.text}
                {m.streaming && <span style={{ opacity: 0.5 }}>▍</span>}
              </div>
            ))}
          </div>

          {/* Input */}
          <div style={{ borderTop: '1px solid #C9B596', padding: 12, display: 'flex', gap: 8, background: '#F5EDDD' }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask anything — sizes, lead times, delivery…"
              data-testid="concierge-input"
              disabled={sending}
              style={{
                flex: 1,
                padding: '10px 14px',
                background: '#FBF6EC',
                border: '1px solid #C9B596',
                borderRadius: 10,
                fontSize: 14,
                color: '#33200F',
                outline: 'none',
              }}
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              data-testid="concierge-send"
              style={{
                background: '#33200F',
                color: '#F5EDDD',
                border: 'none',
                borderRadius: 10,
                padding: '0 14px',
                cursor: sending || !input.trim() ? 'not-allowed' : 'pointer',
                opacity: sending || !input.trim() ? 0.5 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
              }}
              aria-label="Send message"
            >
              <Send size={14} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ConciergeChat;
